// components/entities/pages/organisation-teams/PoolCircles.tsx
import React, { type DragEvent } from 'react';
import UserAvatar from '@/components/UserAvatar';
import { useKernelStore } from '@/kernel/kernelStores';
import { formatCreditsInteger } from '#lib/formatCredits';
import type { LeasingUserDTO } from '#types/types';
import {
  getMemberAvatarPacking,
  getMemberDisplayName,
  selectOrganisationTeamsRuntime,
  type OrganisationPoolNode,
  type TeamPoolNode,
} from './model';

const MEMBER_DRAG_TYPE = 'application/x-fater-organisation-member-id';
const CREDIT_DRAG_TYPE = 'application/x-fater-organisation-credit-source';

const readDataTransfer = (event: DragEvent, type: string): string => {
  try {
    return event.dataTransfer.getData(type).trim();
  } catch {
    return '';
  }
};

const hasDataTransferType = (event: DragEvent, type: string): boolean => {
  try {
    return Array.from(event.dataTransfer.types).includes(type);
  } catch {
    return false;
  }
};

const stopNonPanPointerPropagation = (event: React.PointerEvent<HTMLElement>) => {
  if (event.button === 1) return;
  event.stopPropagation();
};

export const CreditMetric = ({ label, value }: { label: string; value: number }) => (
  <div className="rounded-lg border border-white/10 bg-white/[0.035] px-2 py-1.5">
    <p className="text-[11px] font-medium text-white/40">{label}</p>
    <p className="mt-1 font-mono text-xs text-white/78">{formatCreditsInteger(value)}</p>
  </div>
);

const MemberAvatarNode = React.memo(({ member, organisationId, onSelect, avatarSize, left, top, size, baseZIndex }: {
  member: LeasingUserDTO;
  organisationId: string;
  onSelect: (memberId: string) => void;
  avatarSize: number;
  left: number;
  top: number;
  size: number;
  baseZIndex: number;
}) => {
  const isSelected = useKernelStore((state) => selectOrganisationTeamsRuntime(organisationId, state.inventory.Organisation.registry, state.entityKernelState)?.selectedMemberId === member.id);
  const displayName = getMemberDisplayName(member);
  return (
    <div className="absolute" style={{ left, top, width: size, height: size, zIndex: isSelected ? 1000 : baseZIndex }}>
      <button
        type="button"
        draggable
        title={`${displayName}, ${member.email}`}
        className={`relative flex items-center justify-center rounded-full border p-0.5 transition ${
          isSelected
            ? 'z-20 border-[#3B82F6]/80 bg-[#1E3563]/80 shadow-lg shadow-[#3B82F6]/20'
            : 'border-white/12 bg-white/[0.045] hover:z-10 hover:border-white/25 hover:bg-white/[0.08]'
        }`}
        style={{ width: avatarSize + 4, height: avatarSize + 4 }}
        onPointerDown={stopNonPanPointerPropagation}
        onDragStart={(event) => {
          event.stopPropagation();
          event.dataTransfer.effectAllowed = 'move';
          event.dataTransfer.setData(MEMBER_DRAG_TYPE, member.id);
        }}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onSelect(member.id);
        }}
      >
        <UserAvatar
          userId={member.id}
          name={displayName}
          canEdit={false}
          isOrganisationAdmin={false}
          size={avatarSize}
          shape="circle"
          className="!bg-transparent"
        />
      </button>
    </div>
  );
});
MemberAvatarNode.displayName = 'MemberAvatarNode';

const MemberAvatarCloud = ({ members, organisationId, onSelectMember }: {
  members: LeasingUserDTO[];
  organisationId: string;
  onSelectMember: (memberId: string) => void;
}) => {
  const packing = getMemberAvatarPacking(members.length);
  if (members.length === 0) {
    return <div className="rounded-full border border-dashed border-white/12 bg-white/[0.02]" style={{ width: packing.diameter, height: packing.diameter }} aria-hidden="true" />;
  }

  const center = packing.diameter / 2;
  return (
    <div className="relative" style={{ width: packing.diameter, height: packing.diameter }}>
      {members.length > 1 && (
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/10 bg-white/[0.045] shadow-[0_0_22px_rgba(255,255,255,0.08)]" aria-hidden="true" />
      )}
      {members.map((member, index) => {
        const position = packing.positions[index] ?? { x: 0, y: 0 };
        return (
          <MemberAvatarNode
            key={member.id}
            member={member}
            organisationId={organisationId}
            onSelect={onSelectMember}
            avatarSize={packing.avatarSize}
            left={center + position.x - packing.nodeSize / 2}
            top={center + position.y - packing.nodeSize / 2}
            size={packing.nodeSize}
            baseZIndex={1 + index}
          />
        );
      })}
    </div>
  );
};

const FloatingCreditBadge = ({ title, value, sourceId }: { title: string; value: number; sourceId: string }) => {
  const canDrag = value > 0;
  return (
    <div
      draggable={canDrag}
      title={canDrag ? 'Drag credits to start a transfer' : 'No credits available to transfer'}
      className={`absolute left-1/2 top-0 z-30 flex min-w-[124px] max-w-[200px] -translate-x-1/2 -translate-y-1/2 flex-col items-center rounded-xl border px-3 py-1.5 text-center shadow-2xl shadow-black/40 backdrop-blur transition ${
        canDrag
          ? 'cursor-grab border-[#3B82F6]/28 bg-[#101010]/95 text-white active:cursor-grabbing hover:border-[#3B82F6]/45'
          : 'cursor-not-allowed border-white/10 bg-[#101010]/88 text-white/45'
      }`}
      onPointerDown={stopNonPanPointerPropagation}
      onDragStart={(event) => {
        event.stopPropagation();
        if (!canDrag) {
          event.preventDefault();
          return;
        }
        event.dataTransfer.effectAllowed = sourceId === 'organisation' ? 'copy' : 'move';
        event.dataTransfer.setData(CREDIT_DRAG_TYPE, sourceId);
      }}
    >
      <span className="max-w-full truncate text-[11px] font-semibold leading-tight text-white/88" title={title}>{title}</span>
      <span className="mt-1 whitespace-nowrap font-mono text-[10px] leading-none text-[#3B82F6]/80">{formatCreditsInteger(value)} credits left</span>
    </div>
  );
};

const PoolCircleShell = ({
  x,
  y,
  radius,
  isSelected,
  children,
  onClick,
  onDropMember,
  onCreditDrop,
  targetTeamId,
}: {
  x: number;
  y: number;
  radius: number;
  isSelected: boolean;
  children: React.ReactNode;
  onClick: () => void;
  onDropMember: (memberId: string, targetTeamId: string | null) => void;
  onCreditDrop: (sourceTeamId: string | null, targetTeamId: string | null) => void;
  targetTeamId: string | null;
}) => (
  <div
    role="button"
    tabIndex={0}
    className={`absolute rounded-full border bg-[#101010]/94 text-center shadow-2xl backdrop-blur transition ${
      isSelected ? 'border-[#3B82F6]/55 shadow-[#3B82F6]/10' : 'border-[#2A2A2A] hover:border-white/15 hover:bg-[#141414]'
    }`}
    style={{ left: x, top: y, width: radius * 2, height: radius * 2 }}
    onPointerDown={stopNonPanPointerPropagation}
    onClick={(event) => {
      event.preventDefault();
      event.stopPropagation();
      onClick();
    }}
    onDragOver={(event) => {
      if (hasDataTransferType(event, MEMBER_DRAG_TYPE) || hasDataTransferType(event, CREDIT_DRAG_TYPE)) event.preventDefault();
    }}
    onDrop={(event) => {
      event.preventDefault();
      event.stopPropagation();
      const memberId = readDataTransfer(event, MEMBER_DRAG_TYPE);
      if (memberId) {
        onDropMember(memberId, targetTeamId);
        return;
      }
      const source = readDataTransfer(event, CREDIT_DRAG_TYPE);
      if (source) onCreditDrop(source === 'organisation' ? null : source, targetTeamId);
    }}
  >
    {children}
  </div>
);

export const OrganisationPoolCircle = React.memo(({ node, organisationId, onSelect, onSelectMember, onDropMember, onCreditDrop }: {
  node: OrganisationPoolNode;
  organisationId: string;
  onSelect: () => void;
  onSelectMember: (memberId: string) => void;
  onDropMember: (memberId: string, targetTeamId: string | null) => void;
  onCreditDrop: (sourceTeamId: string | null, targetTeamId: string | null) => void;
}) => {
  const isSelected = useKernelStore((state) => {
    const runtime = selectOrganisationTeamsRuntime(organisationId, state.inventory.Organisation.registry, state.entityKernelState);
    return runtime?.selectedSourcePlanId === 'organisation' && !runtime?.selectedTeamId;
  });
  return (
    <PoolCircleShell
      x={node.x}
      y={node.y}
      radius={node.radius}
      isSelected={isSelected}
      onClick={onSelect}
      onDropMember={onDropMember}
      onCreditDrop={onCreditDrop}
      targetTeamId={null}
    >
      <FloatingCreditBadge title={node.label} value={node.available} sourceId="organisation" />
      <div className="absolute inset-0 flex items-center justify-center overflow-hidden rounded-full">
        <MemberAvatarCloud members={node.members} organisationId={organisationId} onSelectMember={onSelectMember} />
      </div>
    </PoolCircleShell>
  );
});
OrganisationPoolCircle.displayName = 'OrganisationPoolCircle';

export const TeamPoolCircle = React.memo(({ node, organisationId, onSelect, onSelectMember, onDropMember, onCreditDrop }: {
  node: TeamPoolNode;
  organisationId: string;
  onSelect: (teamId: string) => void;
  onSelectMember: (memberId: string) => void;
  onDropMember: (memberId: string, targetTeamId: string | null) => void;
  onCreditDrop: (sourceTeamId: string | null, targetTeamId: string | null) => void;
}) => {
  const isSelected = useKernelStore((state) => selectOrganisationTeamsRuntime(organisationId, state.inventory.Organisation.registry, state.entityKernelState)?.selectedTeamId === node.team.id);
  return (
    <PoolCircleShell
      x={node.x}
      y={node.y}
      radius={node.radius}
      isSelected={isSelected}
      onClick={() => onSelect(node.team.id)}
      onDropMember={onDropMember}
      onCreditDrop={onCreditDrop}
      targetTeamId={node.team.id}
    >
      <FloatingCreditBadge title={node.team.name} value={node.remaining} sourceId={node.team.id} />
      <div className="absolute inset-0 flex items-center justify-center overflow-hidden rounded-full">
        <MemberAvatarCloud members={node.members} organisationId={organisationId} onSelectMember={onSelectMember} />
      </div>
    </PoolCircleShell>
  );
});
TeamPoolCircle.displayName = 'TeamPoolCircle';
